import { makeAutoObservable, runInAction } from "mobx";
import { IFollowUpQuestion } from "../../../models/ReturnReasonTypes";
import ReturnReasonsService from "../../../services/api/return-reasons-service";
import { rootContainer, TYPE } from "../../../services/container/root";
import {ISaveable, WithOld} from "../../../stores/types";
import { ITranslationScopeContext } from "../../../translation/translation-scope";
import { FOLLOW_UP_QUESTION_TYPE, MEDIA_OPTION_TYPE } from "../constants";
import FollowUpQuestionAnswerStore, { IFollowUpQuestionAnswerChanges } from "./follow-up-question-answer-store";
import { cleanupQuestionModel, generateAnswer, generateQuestion } from "./follow-up-question-model";


export interface IFollowUpQuestionChanges extends Omit<Partial<IFollowUpQuestion>, 'merchant_return_reason_fuq_answers'> {
    _client_id?: number|string;
    merchant_return_reason_fuq_answers: IFollowUpQuestionAnswerChanges[];
}
class FollowUpQuestionStore implements ISaveable {
    private _model: IFollowUpQuestion = {} as IFollowUpQuestion;
    private _changes: IFollowUpQuestionChanges = {} as IFollowUpQuestionChanges;
    private _isSaving: boolean = false;
    private _answers: FollowUpQuestionAnswerStore[] = [];

    private _currentTranslationKeys: string[] = [];

    private readonly _translatableFields: string[] = [
        'question',
        'instruction',
        'imageInstruction',
        'stepperHint',
    ];

    private get returnReasonsService(): ReturnReasonsService {
		return rootContainer.get<ReturnReasonsService>(TYPE.ReturnReasonsService);
	}

    constructor(
        private _parentReasonId:number|string, 
        private _discardQuestion: (store: FollowUpQuestionStore) => void, 
        model: IFollowUpQuestion = {} as IFollowUpQuestion) 
    {        
        this._discardAnswer = this._discardAnswer.bind(this);
        makeAutoObservable(this);
        this.setModel(model);
    }

    private setModel(model: IFollowUpQuestion): void {
        this._model = model;
        this._answers = this._model.merchant_return_reason_fuq_answers?.map(answer => {
            return new FollowUpQuestionAnswerStore(this._discardAnswer, answer);
        }) ?? [];
        this._changes = {} as IFollowUpQuestionChanges;
    }

    public setChanges(changes: IFollowUpQuestionChanges): void {
        this._changes = changes;
        this._answers = this._changes.merchant_return_reason_fuq_answers?.map(answer => {
            const answerStore = new FollowUpQuestionAnswerStore(this._discardAnswer);
            answerStore.setChanges(answer);
            return answerStore;
        }) ?? [];
    }

    public isIdEquals(id?:string|number): boolean {
        return this._model.id === Number(id) || this._changes._client_id === (id ?? '').toString();
    }

    public get id() { return this._changes._client_id ?? this._model.id; }
    public get reasonId() { return this._parentReasonId; }
    public get question() { return this._changes.question ?? this._model.question; }
    public get instruction() { return this._changes.instruction ?? this._model.instruction; }

    public get type() { return this._changes.type ?? this._model.type; }
    public get isEnabled() { return this._changes.is_enabled ?? this._model.is_enabled; }
    public get isRequired() { return this._changes.is_required ?? this._model.is_required; }
    public get name() { return this._changes.name ?? this._model.name; }
    public get imageInstruction() { return this._changes.image_instruction ?? this._model.image_instruction; }
    public get isImageRequired() { return Boolean(this._changes.is_image_required ?? this._model.is_image_required); }
    public get isImageRequested() { return Boolean(this._changes.is_image_requested ?? this._model.is_image_requested); }
    public get mediaOption() { return this._changes.media_option ?? this._model.media_option; }
    public get stepperHint() { return this._changes.stepper_hint ?? this._model.stepper_hint; }
    public get stepperDefaultValue() { return this._changes.stepper_default_value ?? this._model.stepper_default_value; }
    public get stepperMinValue() { return this._changes.stepper_min_value ?? this._model.stepper_min_value; }
    public get stepperMaxValue() { return this._changes.stepper_max_value ?? this._model.stepper_max_value; }
    public get delete() { return this._changes._deleted ?? false; }
    public get weight() { return this._changes.weight ?? this._model.weight; }
    public get answers() { return this._answers.slice().sort((a, b) => a.weight - b.weight); }

    public isSaving(): boolean { return this._isSaving; }

    public setIsEnabled(v:boolean) {
        this._changes.is_enabled = v;
    }

    public setIsRequired(v:boolean) {
        this._changes.is_required = v;
        if (!v) {
            /** when the question is not mondatory, the image cannot be mandatory either */
            this.setIsImageRequired(false);
        }
    }

    public setName(v:string) {
        this._changes.name = v;
    }

    public setDeleted(setDeletedTranslationKeys: ITranslationScopeContext['setDeletedKeys']) {
        const keyList = this.getTranslationKeys();
        setDeletedTranslationKeys(keyList);
        
        this._changes._deleted = true;

        this._answers.forEach(store => {
            store.setDeleted(setDeletedTranslationKeys);
        });
    }

    public getTranslationKeys(exclude?: string[]): string[] {
        const keyList: string[] = [];

        // in the case, if we do not need to include keys for deleted items
        if (this.delete) {
            return keyList;
        }

        this._translatableFields.forEach(field => {
            if (exclude && exclude?.includes(field)) {
                return;
            }
            const key = this[field as keyof FollowUpQuestionStore];
            if (key) {
                keyList.push(key.toString());
            }
        });
        // collect translation keys from answers
        this._answers?.forEach(store => {
            keyList.push(...store.getTranslationKeys()); 
        });

        if (this._currentTranslationKeys.length !== keyList.length || !this._currentTranslationKeys.every(k => !!keyList.find(key => key === k))) {
            runInAction(() => {
                this._currentTranslationKeys = keyList;
            });
        } 
        
        return this._currentTranslationKeys;
    }

    public setType(
        type:FOLLOW_UP_QUESTION_TYPE, 
        setDeletedTranslationKeys: ITranslationScopeContext['setDeletedKeys'],
        restoreDeletedTranslationKeys: ITranslationScopeContext['restoreDeletedKeys'])
    {
        if (this.type === type) {
            return;
        }

        /** when switching type - delete all current trans keys */
        setDeletedTranslationKeys(this.getTranslationKeys(['question']));

        /** when switching back to original type, restore model and deleted keys */    
        if (this._model.type === type) {
            this.setModel(this._model);
            restoreDeletedTranslationKeys(this.getTranslationKeys(['question']));
            return;
        }

        const generatedQuestion = generateQuestion(this.id, type, this.weight);
        if (this.question) {
            generatedQuestion.question = this.question;
        }

        const prevName = this.name ?? '';
        this.setChanges(generatedQuestion);
        this.setName(prevName); /** preserve questions name between type changes */
    }

    public setIsImageRequired(v: boolean) { 
        this._changes.is_image_required = v;
    }

    public get isImageRequiredDisabled(): boolean {
        return !this.isRequired || !this.isImageRequested;
    }

    public setIsImageRequested(v: boolean) { 
        this._changes.is_image_requested = v;
        if (!v) {
            /** when image is not requested it cannot be required */
            this.setIsImageRequired(false);
        }
    }
    
    public setMediaOption(v: MEDIA_OPTION_TYPE) { 
        this._changes.media_option = v;
    }

    public setStepperDefaultValue(v:number) {
        this._changes.stepper_default_value = Number(v ?? 0);
    }

    public setStepperMinValue(v:number) {
        this._changes.stepper_min_value = Number(v ?? 0);
    }

    public setStepperMaxValue(v:number) {
        this._changes.stepper_max_value = Number(v ?? 0);
    }

    public getNewWeight() : number {
        return Math.max(...this._answers.map(a => a.weight)) + 1;
    }

    public addAnswer() {
        const answerStore = new FollowUpQuestionAnswerStore(this._discardAnswer);
        const newWeight = this.getNewWeight();
        answerStore.setChanges(generateAnswer(this.id, this.type, newWeight));        
        this._answers?.push(answerStore);
    }
    
    public setWeight(v:number) {
        this._changes.weight = Number(v ?? 0);
    }

    /********* ISaveable implementation *************/ 
    public hasChanges(): boolean {
        const hasAnswerChanges = this._answers?.some(store => store.hasChanges()) ?? false;
        return !!Object.keys(this._changes).length || hasAnswerChanges;
    }

    public commitChanges() {
        throw new Error('question store commitChanges not implemented, not needed, after save the store is reseted with new model');
    }

    public getUpdatedModel(): IFollowUpQuestion {
        const updatedModel = cleanupQuestionModel({
            ...{ ...this._model, ...this._changes },
            merchant_return_reason_fuq_answers: this._answers?.map(store => store.getUpdatedModel())
        });
        return updatedModel;
    }
    
    public async save() {    
        const updatedModel: WithOld<IFollowUpQuestion> = this.getUpdatedModel();

        if (updatedModel.id) {
            updatedModel.__old = this._model;
            const updatedQuestion = await this.returnReasonsService.updateQuestion(this._parentReasonId, updatedModel.id, updatedModel);
            runInAction(() => {
                this.setModel(updatedQuestion);
            })
        } else {
            const createdQuestion = await this.returnReasonsService.createQuestion(this._parentReasonId, updatedModel);
            runInAction(() => {
                this.setModel(createdQuestion);
            })
        }
    }
    
    public discardChanges():void {
        if (!this._model.id) {
            this._discardQuestion(this);
        } 
        else {
            this._changes = {} as IFollowUpQuestion;
            [...this._answers].forEach(store => store.discardChanges());
        }
    }

    /** removes newly created answer store after it was discarded */
    private _discardAnswer(store: FollowUpQuestionAnswerStore): void {
        const storeIndex = this._answers.findIndex(s => s === store);
        if (storeIndex < 0) {
            /** should never happen */
            throw new Error('discardAnswer did not find the store to remove');
        }
        this._answers.splice(storeIndex, 1);
    }
    
}

export default FollowUpQuestionStore;
